import { Avatar } from '@amzn/stencil-react-components/avatar';
import { IconCheck } from '@amzn/stencil-react-components/icons';
import { Text } from '@amzn/stencil-react-components/text';
import React from 'react';
import { EntityType } from 'src/constants/enums';
import { RequiredFields } from 'src/models/RequiredFields';

interface InputElement {
  labelText: string;
  id: string;
  disabled: boolean;
  placeholderText?: string;
}

const viewUserInPhoneTool = ({ data }) => {
  return <Avatar username={data} showImage={false} />;
};

const displayListOfStrings = ({ data }: { data: string[] }) => {
  if (data) {
    return <div>{data.join(',')}</div>;
  }
  return <div></div>;
};

const displayBooleanAsIcon = ({ data }: { data: boolean }) => {
  if (data) {
    return <div>{data ? <IconCheck /> : undefined}</div>;
  }
  return <div></div>;
};

// Status display with color coding: FAILED (red), PARTIAL (orange), SUCCESS (green)
const displayBulkOperationStatus = ({ data }: { data: string }) => {
  if (!data) {
    return <Text>-</Text>;
  }

  const status = data.toUpperCase();

  if (status === 'SUCCESS' || status === 'COMPLETED') {
    return (
      <Text fontWeight="bold" style={{ color: '#1d8102' }}>
        SUCCESS
      </Text>
    );
  }
  if (status === 'FAILED' || status === 'FAILURE') {
    return (
      <Text fontWeight="bold" style={{ color: '#d13212' }}>
        FAILED
      </Text>
    );
  }
  if (status === 'PARTIAL' || status === 'PARTIALLY_COMPLETED') {
    return (
      <Text fontWeight="bold" style={{ color: '#ff9900' }}>
        PARTIAL
      </Text>
    );
  }
  return (
    <Text fontWeight="bold" style={{ color: '#545b64' }}>
      {status}
    </Text>
  );
};

// Result display: "successCount/totalCount Succeeded"
const displayBulkOperationResult = ({ data }: { data: string }) => {
  if (!data) {
    return <Text>-</Text>;
  }
  return <Text>{data} Succeeded</Text>;
};

// Error display: Parse JSON and extract errorMessage field
const displayBulkOperationError = ({ data }: { data: string }) => {
  if (!data) {
    return <Text>-</Text>;
  }

  let errorMessage = data;

  // The commonErrorMessage format is: {JSON} (Service: execute-api; ...)
  // Extract just the JSON portion by finding the matching closing brace
  const jsonEndIndex = data.lastIndexOf('}');
  if (jsonEndIndex !== -1) {
    const jsonString = data.substring(0, jsonEndIndex + 1);
    try {
      const parsed = JSON.parse(jsonString);
      if (parsed.errorMessage) {
        errorMessage = parsed.errorMessage;
      }
    } catch {
      // Fallback: extract errorMessage using regex
      const match = data.match(/"errorMessage"\s*:\s*"([^"]+)"/);
      if (match && match[1]) {
        errorMessage = match[1];
      }
    }
  }

  return <Text style={{ color: '#d13212' }}>{errorMessage}</Text>;
};

export const EditEntityModalInputElements: Record<
  string,
  Array<InputElement>
> = {
  [EntityType.QUEUE]: [
    {
      labelText: 'Queue ID',
      id: 'queueId',
      disabled: true
    },
    {
      labelText: 'Queue Name',
      id: 'name',
      disabled: true
    },
    {
      labelText: 'Description',
      id: 'description',
      disabled: false
    }
  ],
  [EntityType.USER]: [
    {
      labelText: 'User ID',
      id: 'userId',
      disabled: true
    },
    {
      labelText: 'Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Username',
      id: 'userName',
      disabled: false
    },
    {
      labelText: 'Employee ID',
      id: 'employeeId',
      disabled: false
    }
  ],
  [EntityType.PROFILE]: [
    {
      labelText: 'Profile ID',
      id: 'profileId',
      disabled: true
    },
    {
      labelText: 'Profile Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Title',
      id: 'title',
      disabled: false
    },
    {
      labelText: 'Description',
      id: 'description',
      disabled: false
    }
  ],
  [EntityType.PROFILE_PERMISSIONS]: [
    {
      labelText: 'Field ID',
      id: 'fieldId',
      disabled: true
    }
  ],
  [EntityType.USER_GROUPS]: [
    {
      labelText: 'UserGroup Id',
      id: 'userGroupId',
      disabled: true
    },
    {
      labelText: 'UserGroup Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Description',
      id: 'description',
      disabled: false
    }
  ]
};

export const CreateEntityModalInputElements: Record<
  string,
  Array<InputElement>
> = {
  [EntityType.QUEUE]: [
    {
      labelText: 'Queue ID',
      id: 'queueId',
      disabled: false,
      placeholderText: '(Can leave blank)'
    },
    {
      labelText: 'Queue Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Description',
      id: 'description',
      disabled: false
    }
  ],
  [EntityType.USER]: [
    {
      labelText: 'Id',
      id: 'userId',
      disabled: false,
      placeholderText: '(Can leave blank)'
    },
    {
      labelText: 'Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Username',
      id: 'userName',
      disabled: false
    },
    {
      labelText: 'Employee ID',
      id: 'employeeId',
      disabled: false
    }
  ],
  [EntityType.PROFILE]: [
    {
      labelText: 'Profile Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Title',
      id: 'title',
      disabled: false
    },
    {
      labelText: 'Description',
      id: 'description',
      disabled: false
    }
  ],
  [EntityType.PROFILE_PERMISSIONS]: [
    {
      labelText: 'Field ID',
      id: 'fieldId',
      disabled: false
    }
  ],
  [EntityType.USER_GROUPS]: [
    {
      labelText: 'UserGroup Name',
      id: 'name',
      disabled: false
    },
    {
      labelText: 'Description',
      id: 'description',
      disabled: false
    }
  ]
};

export const TableRequiredFields: Record<string, Array<RequiredFields>> = {
  [EntityType.QUEUE]: [
    { header: 'Name', accessor: 'name' },
    { header: 'Queue ID', accessor: 'queueId' },
    { header: 'Description', accessor: 'description' },
    { header: 'Additional Attributes', accessor: 'additionalAttributes' }
  ],
  [EntityType.USER]: [
    { header: 'Name', accessor: 'name' },
    {
      header: 'Username',
      accessor: 'userName',
      cellComponent: viewUserInPhoneTool
    },
    { header: 'User ID', accessor: 'userId' },
    {
      header: 'Employee ID',
      accessor: 'employeeId',
      cellComponent: viewUserInPhoneTool
    },
    {
      header: 'Support Countries',
      accessor: 'supportCountries',
      cellComponent: displayListOfStrings
    },
    {
      header: 'HR For HR',
      accessor: 'isHrForHr',
      cellComponent: displayBooleanAsIcon
    },
    { header: 'Primary Domain', accessor: 'primaryDomain' }
  ],
  [EntityType.PROFILE]: [
    { header: 'Name', accessor: 'name' },
    { header: 'Profile ID', accessor: 'profileId' },
    { header: 'Title', accessor: 'title' },
    { header: 'Description', accessor: 'description' }
  ],
  [EntityType.PROFILE_PERMISSIONS]: [
    { header: 'Field ID', accessor: 'fieldId' },
    { header: 'Permission Type', accessor: 'permissionType' },
    {
      header: 'Permissions',
      accessor: 'permissions',
      cellComponent: displayListOfStrings
    }
  ],
  [EntityType.USER_GROUPS]: [
    { header: 'Name', accessor: 'name' },
    { header: 'User Group Id', accessor: 'userGroupId' },
    { header: 'Description', accessor: 'description' },
    { header: 'Additional Attributes', accessor: 'additionalAttributes' }
  ],
  [EntityType.QUEUE_TO_USER_MAPPINGS]: [
    { header: 'Queue ID', accessor: 'queueId' },
    { header: 'Name', accessor: 'name' },
    { header: 'Employee Ids', accessor: 'employeeIds' }
  ],
  [EntityType.USER_GROUP_TO_USER_MAPPINGS]: [
    { header: 'User Group ID', accessor: 'userGroupId' },
    { header: 'Name', accessor: 'name' },
    { header: 'Employee Ids', accessor: 'employeeIds' }
  ],
  [EntityType.TRD_MAPPINGS]: [
    { header: 'Case Type', accessor: 'CaseType' },
    { header: 'Contact Driver', accessor: 'ContactDriver' },
    { header: 'Country', accessor: 'Country' }
  ],
  [EntityType.CASE_ENTITY]: [
    { header: 'Tracking Id', accessor: 'trackingId' },
    { header: 'Created Date', accessor: 'createdDate' },
    {
      header: 'Status',
      accessor: 'status',
      cellComponent: displayBulkOperationStatus
    },
    { header: 'Downloadable Link', accessor: 'preSignedUrl' }
  ],
  [EntityType.LEGAL_REPORT]: [
    { header: 'Tracking Id', accessor: 'trackingId' },
    { header: 'Created Date', accessor: 'createdDate' },
    { header: 'Status', accessor: 'status' },
    { header: 'Downloadable Link', accessor: 'preSignedUrl' }
  ],
  [EntityType.DELETE_CASE_ENTITY]: [
    { header: 'Tracking Id', accessor: 'trackingId' },
    { header: 'Created Date', accessor: 'createdDate' },
    { header: 'Status', accessor: 'status' },
    { header: 'Downloadable Link', accessor: 'preSignedUrl' }
  ]
};

// Additional columns for bulk case create/update operations
export const BulkOperationResultErrorColumns: Array<RequiredFields> = [
  {
    header: 'Result',
    accessor: 'result',
    cellComponent: displayBulkOperationResult
  },
  {
    header: 'Error',
    accessor: 'error',
    cellComponent: displayBulkOperationError
  }
];

export const BulkFileUploadExpectedHeaders: Record<string, Array<string>> = {
  [EntityType.USER]: [
    'name',
    'userName',
    'employeeId',
    'profileId',
    'primaryDomain',
    'isHrForHr',
    'supportCountries',
    'userId'
  ],
  [EntityType.PROFILE]: ['name', 'title', 'description', 'profileId'],
  [EntityType.QUEUE]: ['name', 'description', 'queueId'],
  [EntityType.QUEUE_TO_USER_MAPPINGS]: ['queueId', 'name', 'employeeIds'],
  [EntityType.USER_GROUP_TO_USER_MAPPINGS]: [
    'userGroupId',
    'name',
    'employeeIds'
  ],
  [EntityType.USER_GROUPS]: ['name', 'description', 'userGroupId'],
  [EntityType.TRD_MAPPINGS]: ['CaseType', 'ContactDriver', 'Country'],
  [EntityType.CASE_ENTITY]: ['caseId', 'caseNumber', 'employeeId'],
  [EntityType.ASSET_ENTITY]: ['caseId', 'assetId', 'assetType', 'content'],
  [EntityType.LEGAL_REPORT]: ['employeeId', 'domainIds'],
  [EntityType.DELETE_CASE_ENTITY]: ['employeeId', 'caseId']
};
